import { forwardRef, Inject, Injectable } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Alert, Metric, Thing, ThingDefinition, ThingTestSession, WorkOrder, WorkSession } from "../model";
import { AbstractThingContextService } from "../shared/class/abstract-thing-context-service.class";
import { FieldService } from "./field.service";

@Injectable()
export class ThingContextService extends AbstractThingContextService {

    private currentThing: Thing;
    private currentThingDefinition: ThingDefinition;
    private currentThingTestSession: ThingTestSession;
    private currentWorkSession: WorkSession;
    private metrics: Promise<Metric[]> = Promise.resolve(null);
    private currentAlert: Alert;
    private currentWorkOrder: WorkOrder;

    static CONTEXT_PERIOD_FIELD_KEY = 'context_period';

    constructor(
        @Inject(forwardRef(() => FieldService)) private fieldService: FieldService,
        @Inject(forwardRef(() => ActivatedRoute)) private activatedRoute: ActivatedRoute
    ) {
        super();
        this.fieldService.register(ThingContextService.CONTEXT_PERIOD_FIELD_KEY, null);
    }

    ngOnDestroy(): void {
        this.fieldService.unregister(ThingContextService.CONTEXT_PERIOD_FIELD_KEY);
    }

    getCurrentThing(): Thing {
        return this.currentThing;
    }

    updateCurrentThing(currentThing: Thing): void {
        this.currentThing = currentThing;
    }

    resetCurrentThing(): void {
        this.currentThing = undefined;
    }

    getCurrentThingDefinition(): ThingDefinition {
        return this.currentThingDefinition;
    }

    updateCurrentThingDefinition(currentThingDefinition: ThingDefinition): void {
        this.currentThingDefinition = currentThingDefinition;
    }

    resetCurrentThingDefinition(): void {
        this.currentThingDefinition = undefined;
    }

    getCurrentThingTestSession(): ThingTestSession {
        return this.currentThingTestSession;
    }

    updateCurrentThingTestSession(currentThingTestSession: ThingTestSession): void {
        this.currentThingTestSession = currentThingTestSession;
    }

    resetCurrentThingTestSession(): void {
        this.currentThingTestSession = undefined;
    }

    getMetrics(): Promise<Metric[]> {
        return this.metrics;
    }

    getMetricByName(name: string): Promise<Metric> {
        return this.metrics.then(metrics => metrics ? metrics.find(m => m.name == name) : null);
    }

    updateMetrics(metrics: Promise<Metric[]>): void {
        this.metrics = metrics;
    }

    resetMetrics(): void {
        this.metrics = Promise.resolve(null);
    }

    updateCurrentWorkSession(currentWorkSession: WorkSession): void {
        this.currentWorkSession = currentWorkSession;
        if (currentWorkSession) {
            const end = currentWorkSession.endTimestamp ? currentWorkSession.endTimestamp : new Date().getTime();
            this.fieldService.updateValue(ThingContextService.CONTEXT_PERIOD_FIELD_KEY, { start: currentWorkSession.startTimestamp, end: end, name: null });
        } else {
            this.fieldService.updateValue(ThingContextService.CONTEXT_PERIOD_FIELD_KEY, null);
        }
    }

    resetCurrentWorkSession(): void {
        this.currentWorkSession = undefined;
        this.fieldService.updateValue(ThingContextService.CONTEXT_PERIOD_FIELD_KEY, null);
    }

    getCurrentWorkSession(): WorkSession {
        return this.currentWorkSession;
    }

    updateCurrentAlert(currentAlert: Alert): void {
        this.currentAlert = currentAlert;
        if (currentAlert) {
            const end = currentAlert.endTimestamp ? currentAlert.endTimestamp : new Date().getTime();
            this.fieldService.updateValue(ThingContextService.CONTEXT_PERIOD_FIELD_KEY, { start: currentAlert.startTimestamp, end: end, name: null });
        } else {
            this.fieldService.updateValue(ThingContextService.CONTEXT_PERIOD_FIELD_KEY, null);
        }
    }

    resetCurrentAlert(): void {
        this.currentAlert = undefined;
        this.fieldService.updateValue(ThingContextService.CONTEXT_PERIOD_FIELD_KEY, null);
    }

    getCurrentAlert(): Alert {
        return this.currentAlert;
    }

    getContextPeriod(context?: any): ContextPeriod {
        let contextPeriod: ContextPeriod = null;
        const now = new Date().getTime();
        const contextEvent = context?.alert || context?.workSession || this.currentAlert || this.currentWorkSession;
        const queryParams = this.activatedRoute.snapshot.queryParams;
        if (contextEvent) {
            contextPeriod = {
                startDate: contextEvent.startTimestamp,
                endDate: contextEvent.endTimestamp ? contextEvent.endTimestamp : now,
                isEndOpen: !contextEvent.endTimestamp
            }
        } else if (queryParams['inputStartTimestamp']) {
            contextPeriod = {
                startDate: queryParams['inputStartTimestamp'],
                endDate: queryParams['inputEndTimestamp'] ? queryParams['inputEndTimestamp'] : now,
                isEndOpen: !queryParams['inputEndTimestamp']
            }
        }
        return contextPeriod;
    }

    getParentThing(): Thing {
        return this.currentThing?.parentThing;
    }

    getRootThing(): Thing {
        return this.currentThing?.rootThing;
    }

    getCurrentWorkOrder(): WorkOrder {
        return this.currentWorkOrder;
    }

    setCurrentWorkOrder(workOrder: WorkOrder): void {
        this.currentWorkOrder = workOrder;
    }
}

export class ContextPeriod {
    startDate: any;
    endDate: any;
    isEndOpen: boolean;
}